import { Button, Checkbox, IconButton } from "@mui/material";
import AdvanceSearch from "core/components/global/advanceSearch/advanceSearch";
import { Toast as showToast } from "core/components/global/toast/toast";
import { useEffect, useState } from "react";
import AddIcon from "@mui/icons-material/Add";
import { Report } from "../../admin.type";
import { v4 as uuidv4 } from "uuid";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";

import {
  SimpleTableContainer,
  AdminModal,
  AdminModalInnerContainer,
  AdminTitle,
  AdminModalBody,
  AdminGroupsForm,
  AdminFormGroup,
  AdminModalButtonContainer,
} from "../../admin.styled";
import AdminGroup from "./report";
import {
  createReport,
  getReports,
  updateReport,
} from "modules/agentMySecurity/services/embeddingsService";
import NoResultsFound from "core/components/global/tables/noResults/noResultsFound";

// Define sort types
type SortColumn = "description" | "active" | "createdAt" | null;
type SortDirection = "asc" | "desc" | null;

export default function AdminReports() {
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [reports, setReports] = useState<Report[]>([]);

  const [schedule, setschedule] = useState("");
  const [recipients, setrecipients] = useState("");
  const [name, setname] = useState("");
  const [description, setdescription] = useState("");
  const [emailSubject, setemailSubject] = useState("");
  const [active, setactive] = useState(false);
  const [copyOfSearchValue, setCopyOfSearchValue] = useState("");

  // Column sorting state
  const [sortColumn, setSortColumn] = useState<SortColumn>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>(null);
  const [filteredReports, setFilteredReports] = useState<Report[] | null>(null);

  // Fetch the reports
  useEffect(() => {
    async function getReportsAsync() {
      try {
        const response = await getReports();
        setReports(response);
      } catch (error) {
        setReports([]);
      }
    }

    getReportsAsync();
  }, []);

  // Remotely update the list of reports
  const handleCreate = async () => {
    // if (name.length > 0 && description.length > 0 && schedule.length > 0 && recipients.length > 0 && emailSubject.length > 0) {
    const formData = {
      _id: uuidv4(),
      schedule: schedule,
      recipients: recipients,
      name: name,
      description: description,
      emailSubject: emailSubject,
      active: active,
      updatedAt: "",
      createdAt: ""
    };

    // Update the user's reports
    if (formData) {
      // Post the updated group
      const response = await createReport(formData);

      if (response !== null) {
        showToast("A report was successfully created", "info");

        // Update the reports, making sure to add at the first index
        const updatedReports = reports;
        updatedReports.unshift(formData);

        // Reset the state for the reports
        setReports(updatedReports);
      } else {
        showToast("Unable to create a report at this time", "error");
      }

      // Close the modal on success or failure
      setModalOpen(false);
    }
    // }
  };

  const handleUpdateNewField = (field: string, value: any) => {
    if (field === "schedule") {
      setschedule(value);
    } else if (field === "recipients") {
      setrecipients(value);
    } else if (field === "name") {
      setname(value);
    } else if (field === "description") {
      setdescription(value);
    } else if (field === "emailSubject") {
      setemailSubject(value);
    } else if (field === "active") {
      setactive(value);
    }
  };

  // Remotely update the list of reports
  const handleUpdateReport = async (formData: Report, reportId: string) => {
    // Update the user's reports
    if (formData) {
      // Post the updated group
      const response = await updateReport(formData, reportId);

      if (response !== null) {
        showToast(response.message, "success");

        const updatedReports = reports.map((report) =>
          report._id === formData._id
            ? {
                ...report,
                ...formData,
              }
            : report
        );

        setReports(updatedReports);
      }
    }
  };

  /**
   * Apply sorting whenever sort parameters change
   */
  useEffect(() => {
    if (!sortColumn || !sortDirection) {
      return; // No sorting applied
    }

    // Apply sorting to the data that's currently displayed
    // (either filtered or all data)
    const dataToSort = [
      ...(filteredReports === null ? reports : filteredReports),
    ];

    const sortedData: any = dataToSort.sort((a, b) => {
      // Default comparison for undefined values
      if (a[sortColumn] === undefined && b[sortColumn] === undefined) return 0;
      if (a[sortColumn] === undefined) return 1;
      if (b[sortColumn] === undefined) return -1;

      let comparison = 0;

      // Special handling for dates
      if (sortColumn === "createdAt") {
        const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
        comparison = dateA - dateB;
      }
      // String comparison for other fields
      else {
        const valueA = String(a[sortColumn]).toLowerCase();
        const valueB = String(b[sortColumn]).toLowerCase();

        if (valueA < valueB) {
          comparison = -1;
        } else if (valueA > valueB) {
          comparison = 1;
        }
      }

      // Reverse for descending order
      return sortDirection === "desc" ? -comparison : comparison;
    });

    // Update the appropriate state based on whether we're sorting filtered or all data
    if (filteredReports === null) {
      setReports(sortedData);
    } else {
      setFilteredReports(sortedData);
    }
  }, [sortColumn, sortDirection]);

  /**
   * Handle column header click to sort
   */
  const handleSort = (column: SortColumn) => {
    // If clicking the same column, toggle direction
    if (sortColumn === column) {
      if (sortDirection === "asc") {
        setSortDirection("desc");
      } else if (sortDirection === "desc") {
        setSortDirection("asc");
      }
    } else {
      // New column, start with ascending
      setSortColumn(column);
      setSortDirection("asc");
    }
  };

  const handleFilterData = (newKeys, b, c) => {
    console.log("handleFilterData");
    setFilteredReports(newKeys);
    // Reset sorting when search changes
    setSortColumn(null);
    setSortDirection(null);
  };

  const handleClearFilter = () => {
    setFilteredReports(null);
  };

  /**
   * Get sort icon for column header
   */
  const getSortIcon = (column: SortColumn) => {
    if (sortColumn !== column) {
      return null;
    }

    return sortDirection === "asc" ? (
      <ArrowUpwardIcon fontSize="small" className="ms-1" />
    ) : (
      <ArrowDownwardIcon fontSize="small" className="ms-1" />
    );
  };

  const handleDeleteReport = (reportId: string) => {
    setReports((prev) => prev.filter((rpt) => rpt._id !== reportId));
    setFilteredReports((prev) =>
      prev ? prev.filter((rpt) => rpt._id !== reportId) : null
    );
  };
  

  return (
    <>
      <SimpleTableContainer elevation={1} columns={3}>
        {/* Data Search */}
        <div className="pb-3 d-flex justify-content-between align-items-center">
          <h5>Reports</h5>
          <AdvanceSearch
            initialData={reports}
            setFilteredData={handleFilterData}
            clearFilteredData={handleClearFilter}
            setCopyOfSearchValue={setCopyOfSearchValue}
          />
        </div>

        {/* Collapsable Table */}
        <div aria-label="collapsible table">
          {/* Headers */}
          <div
            style={{ borderRadius: "5px 5px 0 0" }}
            className="outer-header-container"
          >
            <div className="row-items">
              <p
                className="header-item d-flex align-items-center"
                onClick={() => handleSort("description")}
                style={{ cursor: "pointer" }}
              >
                Description
                {getSortIcon("description")}
              </p>
              <p
                className="header-item d-flex align-items-center"
                onClick={() => handleSort("active")}
                style={{ cursor: "pointer" }}
              >
                Active
                {getSortIcon("active")}
              </p>
              <p
                className="header-item d-flex align-items-center"
                onClick={() => handleSort("createdAt")}
                style={{ cursor: "pointer" }}
              >
                Created At
                {getSortIcon("createdAt")}
              </p>
            </div>
            <div className="row-buttons">
              <IconButton
                aria-label="send prompt"
                onClick={() => setModalOpen(true)}
                edge="end"
                className="me-1 icon-button"
              >
                <AddIcon />
              </IconButton>
            </div>
          </div>

          {/* Body */}
          <div
            style={{ borderRadius: "0 0 5px 5px" }}
            className="outer-row-container"
          >
            {(filteredReports === null ? reports : filteredReports).length > 0 ? (
              filteredReports === null ? (
                <>
                  {reports &&
                    reports.map((report) => (
                      // Row items
                      <AdminGroup
                        key={`row-${report._id}`}
                        report={report}
                        handleUpdateReport={handleUpdateReport}
                        onDelete={handleDeleteReport}
                      />
                    ))}
                </>
              ) : (
                <>
                  {filteredReports.map((report) => (
                    // Row items
                    <AdminGroup
                      key={`row-${report._id}`}
                      report={report}
                      handleUpdateReport={handleUpdateReport}
                      onDelete={handleDeleteReport}
                    />
                  ))}
                </>
              )
            ) : (
              // If no results, show the NoResultsFound component
              <NoResultsFound
                searchTerm={filteredReports !== null ? copyOfSearchValue : ""}
              />
            )}
          </div>
        </div>
      </SimpleTableContainer>

      {/* Modal */}
      <AdminModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <AdminModalInnerContainer elevation={2}>
          {/* Header */}
          <AdminTitle>
            <div className="d-flex">
              <p>Invite Someone</p>
            </div>
          </AdminTitle>

          {/* Body */}
          <AdminModalBody>
            <AdminGroupsForm>
              {/* Name field */}
              <AdminFormGroup>
                <label>Report Name</label>
                <input
                  onChange={(event) =>
                    handleUpdateNewField("name", event.target.value)
                  }
                  placeholder="Enter the report name"
                  name="reportName"
                  value={name}
                />
              </AdminFormGroup>

              {/* Description Field */}
              <AdminFormGroup>
                <label>Description</label>
                <textarea
                  onChange={(event) =>
                    handleUpdateNewField("description", event.target.value)
                  }
                  placeholder="Describe the report"
                  className="recessed"
                  name="description"
                  value={description}
                />
              </AdminFormGroup>

              <AdminFormGroup>
                <label>Schedule</label>
                <textarea
                  onChange={(event) =>
                    handleUpdateNewField("schedule", event.target.value)
                  }
                  placeholder="Enter a cron schedule"
                  name="schedule"
                  className="recessed"
                  value={schedule}
                />
              </AdminFormGroup>

              <AdminFormGroup>
                <label>Recipients</label>
                <textarea
                  onChange={(event) =>
                    handleUpdateNewField("recipients", event.target.value)
                  }
                  placeholder="Enter comma separated recipient email addresses"
                  name="recipients"
                  className="recessed"
                  value={recipients}
                />
              </AdminFormGroup>

              <AdminFormGroup>
                <label>Email Subject</label>
                <textarea
                  onChange={(event) =>
                    handleUpdateNewField("emailSubject", event.target.value)
                  }
                  placeholder="Enter a subject email address"
                  name="emailSubject"
                  className="recessed"
                  value={emailSubject}
                />
              </AdminFormGroup>

              <AdminFormGroup>
                <Checkbox checked={active} />
                <strong className="ms-2">Active</strong>
              </AdminFormGroup>
            </AdminGroupsForm>
          </AdminModalBody>
          <AdminModalButtonContainer>
            <Button variant="contained" onClick={() => handleCreate()}>
              Create Report
            </Button>
          </AdminModalButtonContainer>
        </AdminModalInnerContainer>
      </AdminModal>
    </>
  );
}
